import { useCallback, useRef } from "react";

const WAVEFORM_BAR_COUNT = 34;
export const WAVEFORM_HEIGHT = 74;

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const adjustedRadius = Math.min(radius, width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + adjustedRadius, y);
  context.arcTo(x + width, y, x + width, y + height, adjustedRadius);
  context.arcTo(x + width, y + height, x, y + height, adjustedRadius);
  context.arcTo(x, y + height, x, y, adjustedRadius);
  context.arcTo(x, y, x + width, y, adjustedRadius);
  context.closePath();
  context.fill();
};

const createEmptyVisualizer = () => ({
  analyser: null,
  audioContext: null,
  dataArray: null,
  frameId: null,
  peakLevel: 0,
  source: null,
});

const useLiveMicVisualizer = () => {
  const waveformCanvasRef = useRef(null);
  const micOrbRef = useRef(null);
  const visualizerRef = useRef(createEmptyVisualizer());

  const stop = useCallback(() => {
    const visualizer = visualizerRef.current;

    if (visualizer.frameId) {
      window.cancelAnimationFrame(visualizer.frameId);
    }

    visualizer.source?.disconnect?.();
    visualizer.analyser?.disconnect?.();
    visualizer.audioContext?.close?.().catch(() => {});
    visualizerRef.current = createEmptyVisualizer();

    const canvas = waveformCanvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (micOrbRef.current) {
      micOrbRef.current.style.transform = "";
      micOrbRef.current.style.boxShadow = "";
    }
  }, []);

  const start = useCallback(
    (stream) => {
      stop();

      const canvas = waveformCanvasRef.current;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!canvas || !AudioContextClass) return;

      const context = canvas.getContext("2d");
      if (!context) return;

      const audioContext = new AudioContextClass();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.82;

      const source = audioContext.createMediaStreamSource(stream);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      source.connect(analyser);

      visualizerRef.current = {
        analyser,
        audioContext,
        dataArray,
        frameId: null,
        peakLevel: 0,
        source,
      };

      const drawFrame = () => {
        const activeVisualizer = visualizerRef.current;
        if (!activeVisualizer.analyser || !activeVisualizer.dataArray) return;

        const rect = canvas.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio || 1;
        const canvasWidth = Math.max(1, Math.round(rect.width * pixelRatio));
        const canvasHeight = Math.max(1, Math.round(rect.height * pixelRatio));

        if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
        }

        activeVisualizer.analyser.getByteFrequencyData(activeVisualizer.dataArray);

        let totalLevel = 0;
        const bucketSize = Math.max(
          1,
          Math.floor(activeVisualizer.dataArray.length / WAVEFORM_BAR_COUNT),
        );

        context.clearRect(0, 0, canvasWidth, canvasHeight);
        context.save();
        context.scale(pixelRatio, pixelRatio);

        const width = rect.width;
        const height = rect.height || WAVEFORM_HEIGHT;
        const centerY = height / 2;
        const gap = 3;
        const barWidth = Math.max(
          3,
          (width - gap * (WAVEFORM_BAR_COUNT - 1)) / WAVEFORM_BAR_COUNT,
        );

        for (let index = 0; index < WAVEFORM_BAR_COUNT; index += 1) {
          const bucketStart = index * bucketSize;
          const bucketEnd = Math.min(
            bucketStart + bucketSize,
            activeVisualizer.dataArray.length,
          );
          let bucketTotal = 0;

          for (
            let bucketIndex = bucketStart;
            bucketIndex < bucketEnd;
            bucketIndex += 1
          ) {
            bucketTotal += activeVisualizer.dataArray[bucketIndex];
          }

          const rawLevel = bucketTotal / Math.max(1, bucketEnd - bucketStart) / 255;
          totalLevel += rawLevel;

          const distanceFromCenter =
            Math.abs(index - (WAVEFORM_BAR_COUNT - 1) / 2) /
            (WAVEFORM_BAR_COUNT / 2);
          const centerBoost = 1 - distanceFromCenter * 0.36;
          const shapedLevel = Math.pow(rawLevel, 0.62) * centerBoost;
          const idleLift = 0.08 + Math.sin(Date.now() / 260 + index * 0.75) * 0.025;
          const barHeight = Math.max(
            8,
            (height - 10) * (idleLift + shapedLevel * 0.78),
          );
          const x = index * (barWidth + gap);
          const y = centerY - barHeight / 2;

          const gradient = context.createLinearGradient(0, y, 0, y + barHeight);
          gradient.addColorStop(0, "rgba(70, 184, 255, 0.95)");
          gradient.addColorStop(0.52, "rgba(57, 168, 240, 0.82)");
          gradient.addColorStop(1, "rgba(34, 142, 215, 0.55)");
          context.fillStyle = gradient;
          context.globalAlpha = 0.52 + Math.min(0.42, shapedLevel);
          drawRoundedRect(context, x, y, barWidth, barHeight, 999);
        }

        context.restore();

        const averageLevel = totalLevel / WAVEFORM_BAR_COUNT;
        activeVisualizer.peakLevel = Math.max(
          averageLevel,
          activeVisualizer.peakLevel * 0.9,
        );

        if (micOrbRef.current) {
          const orbScale = 1 + Math.min(0.14, activeVisualizer.peakLevel * 0.34);
          const glow = 0.32 + Math.min(0.34, activeVisualizer.peakLevel * 0.8);
          micOrbRef.current.style.transform = `scale(${orbScale.toFixed(3)})`;
          micOrbRef.current.style.boxShadow = `0 18px 38px rgba(57, 168, 240, ${glow.toFixed(3)})`;
        }

        activeVisualizer.frameId = window.requestAnimationFrame(drawFrame);
      };

      drawFrame();
    },
    [stop],
  );

  return {
    micOrbRef,
    start,
    stop,
    waveformCanvasRef,
  };
};

export default useLiveMicVisualizer;
